import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { fetchSubmissionStatus } from "../lib/api";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function SubmitSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const location = useLocation();
    const freeEvent = location.state?.event || null;

    const [state, setState] = useState(() =>
        freeEvent
            ? {
                  status: "paid",
                  event: freeEvent,
                  message: "Your event is live on the Virginia map.",
              }
            : sessionId
              ? {
                    status: "polling",
                    event: null,
                    message: "Confirming your payment…",
                }
              : {
                    status: "paid",
                    event: null,
                    message: "Your event has been submitted.",
                },
    );
    const attemptsRef = useRef(0);
    const stoppedRef = useRef(false);

    useEffect(() => {
        // Skip stripe polling for the free-submission flow
        if (freeEvent || !sessionId) return;

        const poll = async () => {
            if (stoppedRef.current) return;
            attemptsRef.current += 1;
            try {
                const data = await fetchSubmissionStatus(sessionId);
                if (data.payment_status === "paid") {
                    stoppedRef.current = true;
                    setState({
                        status: "paid",
                        event: data.event,
                        message: "Payment received — your event is live.",
                    });
                    return;
                }
                if (data.status === "expired") {
                    stoppedRef.current = true;
                    setState({
                        status: "expired",
                        message:
                            "Payment session expired. Please submit again.",
                    });
                    return;
                }
                if (attemptsRef.current >= MAX_ATTEMPTS) {
                    stoppedRef.current = true;
                    setState({
                        status: "timeout",
                        message:
                            "Still processing — refresh this page in a moment, or check your email for confirmation.",
                    });
                    return;
                }
                setTimeout(poll, POLL_INTERVAL);
            } catch (err) {
                stoppedRef.current = true;
                setState({
                    status: "error",
                    message:
                        err?.response?.data?.detail ||
                        "Could not verify payment status.",
                });
            }
        };
        poll();

        return () => {
            stoppedRef.current = true;
        };
    }, [sessionId, freeEvent]);

    return (
        <div
            className="min-h-screen bg-[#0A0A0A] text-white font-manrope flex items-center justify-center px-6"
            data-testid="submit-success-page"
        >
            <div className="max-w-xl w-full border border-white/10 bg-[#0d0d0d] p-8 text-center">
                <StatusIcon status={state.status} />

                <h1 className="font-anton text-4xl sm:text-5xl uppercase mt-4 leading-tight">
                    {state.status === "paid"
                        ? "You're on the map."
                        : state.status === "polling"
                          ? "Hold tight."
                          : "Hmm."}
                </h1>

                <p
                    className="text-sm text-zinc-400 mt-3"
                    data-testid="submit-status-message"
                >
                    {state.message}
                </p>

                {state.event && (
                    <div className="mt-6 border border-white/10 bg-black/30 p-4 text-left">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">
                            Now live
                        </div>
                        <div className="font-anton text-2xl uppercase">
                            {state.event.title}
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">
                            {state.event.county} · {state.event.start_date}
                        </div>
                    </div>
                )}

                <div className="flex flex-wrap gap-2 justify-center mt-8">
                    <Link
                        to={
                            state.event?.county
                                ? `/?county=${encodeURIComponent(
                                      state.event.county,
                                  )}`
                                : "/"
                        }
                        className="inline-flex items-center gap-1.5 bg-[#FF3B30] hover:bg-[#FF574D] text-white font-anton tracking-widest uppercase text-sm px-5 py-3"
                        data-testid="view-event-btn"
                    >
                        See it on the map <ArrowRight size={14} />
                    </Link>
                    <Link
                        to="/submit"
                        className="inline-flex items-center px-5 py-3 border border-white/15 hover:border-white text-xs uppercase tracking-[0.22em] font-semibold"
                        data-testid="submit-another"
                    >
                        Submit another
                    </Link>
                </div>
            </div>
        </div>
    );
}

function StatusIcon({ status }) {
    if (status === "paid")
        return <CheckCircle2 size={56} className="mx-auto text-[#FF3B30]" />;
    if (status === "polling")
        return (
            <Loader2
                size={56}
                className="mx-auto text-zinc-400 animate-spin"
            />
        );
    return <AlertTriangle size={56} className="mx-auto text-yellow-500" />;
}
